/**
 * Service for subscription related operations.
 *
 * All controllers should use this service to initiate an operation and not the Payments service
 * as it controls the sequence of steps that lead to a change in a subscription.
 *
 * The actual API calls to update a subscription are done in the Payments service.
 *
 */

import { PaymentData } from '@hotjar/webapp/billing/plan';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import { ordersApi } from '@hotjar/webapp/domains/billing/orders';
import { checkout, openCompanyDetailsModal, openPaymentDetails } from '@hotjar/checkout';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { useStickyNotificationStore } from '@hotjar/navigation';
import { upsell as openUpsellModal } from '@hotjar/webapp/domains/revenue/upsell';
import { mapSiteDtoToModel } from '@hotjar/site';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { mapOwnedAccountDtoToModel } from '@hotjar/account';

angular
    .module('hjApp')
    .service(
        'Subscription',
        function (
            $filter,
            $q,
            $rootScope,
            $state,
            Account,
            ModalBillingWarning,
            Notify,
            Payments,
            restDefaultHandlers,
            Site,
            UserData
        ) {
            /*************************
             * PRIVATE FUNCTIONS
             *************************/

            /**
             * Show a billing warning message.
             *
             * @param warningType Warning type.
             * @param account Account account alert relates to.
             * @param subscriptionInfo Account's subscription information.
             */
            function showBillingWarningAlert(warningType, account, subscriptionInfo) {
                var data = {
                        account: account,
                        isAccountOwner: Account.isOwner(account),
                        subscriptionInfo: subscriptionInfo
                    },
                    warningMessage;

                if (warningType === 'recurring-payment-failed') {
                    warningMessage = data.isAccountOwner
                        ? 'We were unable to process the payment for your subscription.'
                        : "We were unable to process the payment for this site's subscription.";
                } else if (warningType === 'card-expiring') {
                    warningMessage = data.isAccountOwner
                        ? 'Your card will soon expire. Update it now for uninterrupted service.'
                        : 'The card used for this site’s subscription will soon expire and needs to be updated.';
                }

                Account.getSites(account, null, function (site) {
                    return site.plan !== 'basic';
                }).then(function (sites) {
                    data.sites = sites;
                    useStickyNotificationStore.getState().show('subscription-issue', {
                        text: warningMessage,
                        kind: 'danger',
                        isDismissible: false,
                        actionText: 'Learn more',
                        onAction: () => ModalBillingWarning.show(warningType, data)
                    });
                }, restDefaultHandlers.onError);
            }

            /**
             * Check if the subscription's credit card is about to expire.
             *
             * @param cardExpirationDate Card's expiration date (epoch timestamp)
             * @returns {boolean} True if credit card is about to expire, False otherwise.
             */
            function isCardExpiringSoon(cardExpirationDate) {
                return !!(cardExpirationDate && $filter('daysFromNow')(cardExpirationDate) <= 30);
            }

            /**
             * Function to couple authenticating a new payment method, or existing
             * payment method, based on changes introduced by the given cost specs
             * and potentially the billing cycle.
             *
             * This is different to Subscription.update as its purpose is as a
             * straightforward upsert to be used by the payment details modal,
             * rather than the whole update flow which would handle showing the
             * company details modal or the payment details modal, this is simply
             * attempting the upsert.
             *
             * We also don't want to duplicate the calls to `getPaymentMethod` or
             * `preview` if we can help it, so we only call these twice if going
             * through the payment details modal. We could make this more efficient
             * by checking if the payment method is indeed changing or if the
             * billing cycle has changed.
             *
             * @param {Account} account The Account whose Subscription is being updated.
             * @param {CostSpec[]} costSpecs The list of changes being applied to
             * the Account's Subscription
             * @param {VaultedPaymentMethod} [paymentMethod] The payment method to
             * use for the Subscription, if not given we'll default to the Account's
             * payment method.
             * @param {string} [billingCycle] The billing cycle to change to, if
             * desired
             *
             * @returns {Promise} The result of the upsert request.
             */
            function upsertSubscription(account, costSpecs, paymentMethod = undefined, billingCycle = undefined) {
                // Ensure we have a payment method to authenticate, either its been
                // provided or we have to get it from the Account (in the case where
                // we're choosing to keep the old payment method)
                const paymentMethodRequest = !_.isUndefined(paymentMethod)
                    ? $q.resolve(paymentMethod)
                    : Payments.getPaymentMethod(account);

                // Check how much the changes are going to cost, and potentially a
                // change to the billing cycle, for authentication purposes
                const previewRequest = subscriptionAPI.preview(account, costSpecs, billingCycle);

                return $q
                    .all({
                        paymentMethod: paymentMethodRequest,
                        priceSummary: previewRequest
                    })
                    .then(({ paymentMethod, priceSummary }) =>
                        Payments.authenticatePaymentMethod(account, paymentMethod, priceSummary.future.totalWithTax)
                    )
                    .then((authenticatedPaymentMethod) =>
                        Payments.upsertSubscription(account, costSpecs, authenticatedPaymentMethod, billingCycle)
                    );
            }

            /**
             * Show the payment modal.
             *
             * @param {object} data
             * @param {Account} data.account Account to update.
             * @param {PaymentData} data.paymentData Payment data to pass on to the payment modal.
             * @param {boolean} data[editPaymentMethod] Whether to open with the payment method set as editable.
             * @param {string} data[errorMessage] If there's an error message to show.
             * @returns {Promise<PaymentModal>} A resolution.
             */
            function showPaymentModal({ account, paymentData, errorMessage = undefined }) {
                // for now we don't support updating payment method through zuora, so just passing false
                return openPaymentDetails(false, {
                    account: mapOwnedAccountDtoToModel(account),
                    costSpecs: paymentData.costSpecs,
                    referrer: paymentData.referrer,
                    initialBillingCycle: paymentData.defaultBillingCycle,
                    initialErrorMessage: errorMessage,
                    onPaymentMethodVaulted: ({ vaultedPaymentMethod, billingCycle }) =>
                        upsertSubscription(account, paymentData.costSpecs, vaultedPaymentMethod, billingCycle)
                });
            }

            /**
             * Show the Company Details modal and, if it's completed successfully, show the Payment Modal.
             *
             * @param {Account} account The account to set company details.
             * @param {PaymentData} [paymentData] The payment data to pass through to the payment modal.
             * @returns {Promise} A resolution.
             */
            function showCompanyDetails(account, paymentData) {
                return openCompanyDetailsModal()
                    .then((companyDetails) => {
                        return showPaymentModal({ account, paymentData }).then((paymentMethod) => {
                            const isZuoraEabled = isZuoraEnabled(account);

                            if (!isZuoraEabled) {
                                return paymentMethod;
                            }

                            const { companyName, taxNumber, ...billingAddress } = companyDetails;
                            const createOrderRequest = {
                                orderAction: 'customer_and_subscription_creation_order',
                                subscription: {
                                    billingPeriod: paymentMethod.billingCycle,
                                    sitePlans: (paymentData.costSpecs ?? []).map(({ site_id, plan_id }) => ({
                                        siteId: site_id,
                                        planId: plan_id
                                    }))
                                },
                                customer: {
                                    companyName,
                                    taxNumber,
                                    billingAddress,
                                    additionalEmailAddresses: [], // TODO: add
                                    paymentMethod: {
                                        // TODO: for now zuora only supports credit card, this will change
                                        paymentMethodType: 'credit_card',
                                        paymentMethodId: paymentMethod.refId
                                    }
                                }
                            };

                            return ordersApi.createNewOrder(account.id, createOrderRequest);
                        });
                    })
                    .finally(UserData.refreshFromServer);
            }

            function notifyInvoiceErrorWithMessage(message) {
                useStickyNotificationStore.getState().show('subscription-issue', {
                    text: message,
                    kind: 'danger',
                    isDismissible: false,
                    actionText: 'Invoices page.',
                    onAction: _.partial($state.go, 'settings.invoices')
                });
            }

            function notifyPlansAndBillingErrorWithMessage(message, actionText) {
                useStickyNotificationStore.getState().show('subscription-issue', {
                    text: message,
                    kind: 'danger',
                    isDismissible: true,
                    actionText,
                    onAction: _.partial($state.go, 'settings.billing')
                });
            }

            /********************
             * PUBLIC ACCESS FUNCTIONS
             ********************/

            /**
             * Update (or create) an Account's Subscription.
             *
             * This function encapsulates our unified flow for updating or creating
             * a Subscription. This includes the logic of "can we perform the update
             * without a payment method" in the case of downgrades for example, or
             * ensuring we have a Payment Method via the Payment Details modal.
             *
             * @param {Account} account The Account whose Subscription we're updating.
             * @param {PaymentData} paymentData Context object which contains data related to how to
             * update the Subscription.
             * @param {VaultedPaymentMethod} [newPaymentMethod] The new payment method if updating it.
             * @param {string} [billingCycle] If we're changing the billing cycle then the cycle to
             * change to.
             * @param {boolean} [reload] Whether we want to reload the page at the end of update flow
             * change to.
             * @returns {Promise} The resolution of plan changes.
             *
             * @deprecated use checkout fn directly
             * @see checkout
             */
            this.update = function update(
                account,
                paymentData = new PaymentData(),
                newPaymentMethod = undefined,
                billingCycle = undefined,
                showPlanChangeConfirmation = false
            ) {
                return checkout({
                    // convert to OwnedAccount model in case any legacy code sends down the dto
                    account: mapOwnedAccountDtoToModel(account),
                    paymentData,
                    paymentMethod: newPaymentMethod,
                    billingCycle,
                    showPlanChangeConfirmation
                }).then(() => {
                    UserData.refreshFromServer();
                });
            };

            /**
             * Initiate the flow to update the payment method for an existing customer.
             *
             * @param account Account to update payment method for.
             * @param paymentData To be passed along to the modal to display the currentPaymentMethod.
             * @param editPaymentMethod Boolean indicating which state to open the modal in.
             * @returns {Promise} A resolution.
             */
            this.updatePaymentMethod = function updatePaymentMethod(
                account,
                paymentData = new PaymentData(),
                editPaymentMethod = true
            ) {
                return Payments.getPaymentMethod(account).then(
                    // If we have Payment Details set, we only need to show the payment details
                    // modal for editing.
                    //
                    // TODO(rob.lester): Pass the result as the 'current' payment
                    // method to the modal.
                    () => showPaymentModal({ account, paymentData, editPaymentMethod }),
                    // If we have no payment details set, we need to create a new subscription.
                    // Flow:
                    // 1. Open the Company Details modal so the user can confirm details such as country and VAT #.
                    // 2. Open the Payment modal where the user can input payment details.
                    () => showCompanyDetails(account, paymentData)
                );
            };

            /**
             * Initiate the flow to delete the payment method for an existing customer.
             *
             * @param account Account to delete payment method for.
             */
            this.deletePaymentMethod = function (account) {
                Payments.deletePaymentMethod(account.id).then(function () {
                    Notify.success('Payment method removed.');
                    $rootScope.$emit('paymentMethodChanged');
                });
            };

            /**
             * Initiate the flow to upsell a site.
             *
             * @param referrer: Referring action to upsell the site.
             * @param upsellType: The type of Upsell to perform.
             * @param site: [optional] Site to Upsell – defaults to current site.
             * @param requestedPlanId: [optional] Plan ID being requested.
             * @param isUpgradeRequest: [optional] Whether or not it's an upgrade request initiated by a non-owner.
             */
            this.upsell = function upsell(referrer, site, requestedPlanId, isUpgradeRequest) {
                site = site || UserData.currentSite;

                return openUpsellModal({
                    site: mapSiteDtoToModel(site),
                    referrer,
                    requestedPlanId,
                    isUpgradeRequest
                }).then(_.noop, _.noop);
            };

            /**
             * Gets all the subscription / billing information associated to an account, such as:
             * Payment method, currency, next billing date, etc.
             *
             * @param account Account to get details for.
             * @return object containing all the data associated to an account.
             */
            this.getInfo = function (account) {
                if (account) {
                    return Payments.getDetails(account.id).then(
                        function (res) {
                            var subscriptionInfo, prices;

                            prices = {
                                currency: res.currency,
                                symbol: window.settings.currency_symbols[res.currency],
                                vatRate: Number(res.vat_rate)
                            };
                            // TODO(rob.lester): Delete all this price stuff, we
                            // need to call the subscription preview (or pricing)
                            // API for more accurate numbers. These numbers won't
                            // include the right deductions.
                            if (res.billing_cycle === 'yearly_recurring') {
                                prices.total = Number(res.yearly_subtotal) + Number(res.yearly_vat_amount);
                                prices.vatTotal = Number(res.yearly_vat_amount);
                                prices.subtotal = Number(res.yearly_subtotal);
                            } else {
                                prices.total = Number(res.monthly_subtotal) + Number(res.monthly_vat_amount);
                                prices.vatTotal = Number(res.monthly_vat_amount);
                                prices.subtotal = Number(res.monthly_subtotal);
                            }
                            subscriptionInfo = {
                                accountOwner: res.account_owner,
                                billingCycle: res.billing_cycle,
                                nextBillingDate: res.next_billing_date,
                                hasPaidPlan: res.has_paid_plan,
                                paymentMethod: {
                                    available: res.payment_method_type !== null,
                                    expiryDate: res.card_expiration_date,
                                    label: res.payment_method_label,
                                    type: res.payment_method_type
                                },
                                prices: prices,
                                recurringPayment: {
                                    failureCount: res.failure_count,
                                    lastFailureDate: res.failure_datetime
                                },
                                warnings: {
                                    cardExpiringSoon: isCardExpiringSoon(res.card_expiration_date),
                                    pastDue: res.has_paid_plan && res.failure_count > 0
                                }
                            };
                            return subscriptionInfo;
                        },
                        function (res) {
                            Notify.unhandledError();
                            return $q.reject(res);
                        }
                    );
                }
                return $q.reject();
            };

            /**
             * Show subscription sticky notification alert if applicable.
             *
             * @param account Account to check for.
             * @param subscriptionInfo Account's subscription information.
             */
            this.showAlerts = function showAlerts(account, subscriptionInfo) {
                var alertShowing = true,
                    isAccountOwner = Account.isOwner(account),
                    trial = Account.getTrialInfo(account),
                    showWarningAlert = _.partial(showBillingWarningAlert, _, account, subscriptionInfo);

                const canAccessBillingV3 = isBillingV3Enabled(account);

                if (
                    isAccountOwner &&
                    trial.activeOrEligible &&
                    account.has_paid_plan &&
                    trial.daysRemaining &&
                    trial.daysRemaining < 5
                ) {
                    // Account is on trial and will end in less than 5 days.
                    const actionText = canAccessBillingV3 ? "Go to 'Subscription'" : "Go to 'Plans & Billing'";
                    notifyPlansAndBillingErrorWithMessage('Your trial ends ' + trial.timeUntilExpiry + '.', actionText);
                } else if (subscriptionInfo.warnings.pastDue && !canAccessBillingV3) {
                    // Subscription is past due.
                    showWarningAlert('recurring-payment-failed');
                } else if (subscriptionInfo.warnings.cardExpiringSoon && !canAccessBillingV3) {
                    // Card used for subscription is soon expiring.
                    showWarningAlert('card-expiring');
                } else if (isAccountOwner && account.lock) {
                    // Account is locked due to a pending payment.
                    notifyInvoiceErrorWithMessage(
                        'You have one outstanding invoice. Find all of your invoices on your '
                    );
                } else {
                    // Else hide any sticky notifications showing.
                    // This is done since UserData could change and we want to make sure we hide
                    // any  notifications that were originally showing.
                    useStickyNotificationStore.getState().hide('subscription-issue');

                    alertShowing = false;
                }

                // Return a boolean as to whether we are showing an alert or not.
                return alertShowing;
            };
        }
    );
